import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Button,
    Card,
    Empty,
    Form,
    Input,
    Modal,
    Skeleton,
    Space,
    Table,
    Tag,
    Tooltip,
    Typography,
    message,
} from 'antd';
import {
    CameraOutlined,
    HistoryOutlined,
    RollbackOutlined,
    WarningOutlined,
} from '@ant-design/icons';
import { useAuth } from '../../hooks/useAuth';
import { aiAdminApi, GlobalSnapshot } from '../../services/ai-admin-api';
import { Utils } from '../../utils/utils';

const { Title, Text, Paragraph } = Typography;

/**
 * Phase 5 — global-snapshot management. The backend (SnapshotsService +
 * SnapshotsController) was built in Phase 1 / 4a; this page provides the
 * admin UI: list, create, restore. Restore is SUPER_ADMIN-only, gated both
 * client-side (button hidden / disabled) and server-side (SuperAdminOnlyGuard
 * on /admin/ai/snapshots/:id/restore).
 */
export const SnapshotsPage: React.FC = () => {
    const { user } = useAuth();
    const jwt = user?.jwtToken ?? '';
    const isSuperAdmin = user ? Utils.userIsSuperAdmin(user) : false;

    const [snapshots, setSnapshots] = useState<GlobalSnapshot[]>([]);
    const [loading, setLoading] = useState(false);
    const [creating, setCreating] = useState(false);
    const [restoring, setRestoring] = useState<GlobalSnapshot | null>(null);

    const reload = useCallback(async () => {
        if (!jwt) return;
        setLoading(true);
        try {
            setSnapshots(await aiAdminApi.listSnapshots(jwt));
        } catch (err: any) {
            message.error(err.message ?? 'Failed to load snapshots');
        } finally {
            setLoading(false);
        }
    }, [jwt]);

    useEffect(() => {
        reload();
    }, [reload]);

    return (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <div>
                <Title level={2} style={{ marginBottom: 4 }}>
                    <CameraOutlined style={{ marginRight: 8 }} />
                    AI snapshots
                </Title>
                <Text type="secondary">
                    Atomic point-in-time captures of every subsystem&apos;s
                    currently-published version. Restore rolls all 11
                    subsystems back to those versions in a single transaction
                    — if any subsystem fails the whole restore aborts.
                </Text>
            </div>

            <Alert
                type="info"
                showIcon
                message="When to use a snapshot"
                description={
                    <ul style={{ margin: 0, paddingLeft: 20 }}>
                        <li>
                            Before a high-risk multi-subsystem change, so you
                            have a known-good state to roll back to.
                        </li>
                        <li>
                            After a major release as the &ldquo;v1.0&rdquo;
                            baseline.
                        </li>
                        <li>
                            Before publishing a draft you&apos;re unsure about
                            — the per-subsystem version history is enough for
                            single-subsystem rollbacks, but a snapshot covers
                            the whole platform at once.
                        </li>
                    </ul>
                }
            />

            <Card
                title={
                    <span>
                        <HistoryOutlined style={{ marginRight: 8 }} />
                        Saved snapshots
                    </span>
                }
                extra={
                    <Button
                        type="primary"
                        icon={<CameraOutlined />}
                        onClick={() => setCreating(true)}
                    >
                        Capture snapshot
                    </Button>
                }
                styles={{ body: { padding: 12 } }}
            >
                {loading && snapshots.length === 0 ? (
                    <Skeleton active />
                ) : snapshots.length === 0 ? (
                    <Empty description="No snapshots yet — capture one to baseline the current state.">
                        <Button
                            type="primary"
                            icon={<CameraOutlined />}
                            onClick={() => setCreating(true)}
                        >
                            Capture snapshot
                        </Button>
                    </Empty>
                ) : (
                    <Table<GlobalSnapshot>
                        size="small"
                        rowKey="id"
                        pagination={{ pageSize: 25 }}
                        dataSource={snapshots}
                        columns={[
                            {
                                title: 'Name',
                                dataIndex: 'name',
                                render: (n: string) => <Text strong>{n}</Text>,
                            },
                            {
                                title: 'Subsystems captured',
                                key: 'subsystems',
                                width: 200,
                                render: (_, row) => (
                                    <Tag>
                                        {Object.keys(
                                            row.versionPointersJson ?? {},
                                        ).length}{' '}
                                        subsystems
                                    </Tag>
                                ),
                            },
                            {
                                title: 'Notes',
                                dataIndex: 'notes',
                                render: (v: string | null) =>
                                    v ?? <Text type="secondary">—</Text>,
                                ellipsis: true,
                            },
                            {
                                title: 'Captured',
                                dataIndex: 'createdAt',
                                width: 180,
                                render: (v: string) =>
                                    new Date(v).toLocaleString(),
                            },
                            {
                                title: 'Actions',
                                key: 'actions',
                                width: 160,
                                render: (_, row) => (
                                    <Tooltip
                                        title={
                                            isSuperAdmin
                                                ? 'Roll every subsystem back to this snapshot'
                                                : 'Restore is SUPER_ADMIN only'
                                        }
                                    >
                                        <Button
                                            size="small"
                                            danger
                                            icon={<RollbackOutlined />}
                                            disabled={!isSuperAdmin}
                                            onClick={() => setRestoring(row)}
                                        >
                                            Restore
                                        </Button>
                                    </Tooltip>
                                ),
                            },
                        ]}
                    />
                )}
            </Card>

            {creating && (
                <CreateSnapshotModal
                    jwt={jwt}
                    onClose={() => setCreating(false)}
                    onCreated={() => {
                        setCreating(false);
                        reload();
                    }}
                />
            )}
            {restoring && isSuperAdmin && (
                <RestoreSnapshotModal
                    jwt={jwt}
                    snapshot={restoring}
                    onClose={() => setRestoring(null)}
                    onRestored={() => {
                        setRestoring(null);
                        reload();
                    }}
                />
            )}
        </Space>
    );
};

interface CreateSnapshotModalProps {
    jwt: string;
    onClose: () => void;
    onCreated: () => void;
}

const CreateSnapshotModal: React.FC<CreateSnapshotModalProps> = ({
    jwt,
    onClose,
    onCreated,
}) => {
    const [form] = Form.useForm();
    const [submitting, setSubmitting] = useState(false);

    const submit = async () => {
        const values = await form.validateFields();
        setSubmitting(true);
        try {
            await aiAdminApi.createSnapshot(
                jwt,
                values.name.trim(),
                values.notes?.trim() || undefined,
            );
            message.success('Snapshot captured');
            onCreated();
        } catch (err: any) {
            message.error(err.message ?? 'Capture failed');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal
            open
            width={560}
            title="Capture AI snapshot"
            okText="Capture"
            onCancel={onClose}
            onOk={submit}
            confirmLoading={submitting}
        >
            <Paragraph type="secondary">
                Records the currently-published version pointer for every
                subsystem. The capture is instant — no LLM calls or recompute.
            </Paragraph>
            <Form form={form} layout="vertical">
                <Form.Item
                    label="Name"
                    name="name"
                    rules={[{ required: true, message: 'Name is required' }]}
                >
                    <Input placeholder="e.g. v1.0 launch baseline" />
                </Form.Item>
                <Form.Item label="Notes (optional)" name="notes">
                    <Input.TextArea
                        rows={3}
                        placeholder="Why this snapshot? What's about to change?"
                    />
                </Form.Item>
            </Form>
        </Modal>
    );
};

interface RestoreSnapshotModalProps {
    jwt: string;
    snapshot: GlobalSnapshot;
    onClose: () => void;
    onRestored: () => void;
}

const RestoreSnapshotModal: React.FC<RestoreSnapshotModalProps> = ({
    jwt,
    snapshot,
    onClose,
    onRestored,
}) => {
    const [form] = Form.useForm();
    const [submitting, setSubmitting] = useState(false);
    const [confirmName, setConfirmName] = useState('');
    const subsystemCount = Object.keys(
        snapshot.versionPointersJson ?? {},
    ).length;
    const nameMatches = useMemo(
        () => confirmName.trim() === snapshot.name,
        [confirmName, snapshot.name],
    );

    const submit = async () => {
        const values = await form.validateFields().catch(() => null);
        setSubmitting(true);
        try {
            await aiAdminApi.restoreSnapshot(
                jwt,
                snapshot.id,
                values?.notes?.trim() || undefined,
            );
            message.success('Snapshot restored');
            onRestored();
        } catch (err: any) {
            message.error(err.message ?? 'Restore failed');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal
            open
            width={640}
            title={
                <span style={{ color: '#cf1322' }}>
                    <WarningOutlined style={{ marginRight: 8 }} />
                    Restore snapshot
                </span>
            }
            okText="Restore now"
            okButtonProps={{
                danger: true,
                disabled: !nameMatches,
            }}
            onCancel={onClose}
            onOk={submit}
            confirmLoading={submitting}
        >
            <Alert
                type="warning"
                showIcon
                message="This will roll every subsystem back"
                description={`All ${subsystemCount} subsystems captured by this snapshot will be rolled back to their respective versions. The operation is atomic — if any subsystem fails, the whole restore aborts and nothing changes. Cache invalidation runs only on successful commit, so customer-facing dashboards refresh immediately.`}
                style={{ marginBottom: 16 }}
            />
            <Paragraph>
                <Text strong>Snapshot:</Text> {snapshot.name}
                <br />
                <Text strong>Captured:</Text>{' '}
                {new Date(snapshot.createdAt).toLocaleString()}
                {snapshot.notes && (
                    <>
                        <br />
                        <Text strong>Notes:</Text> {snapshot.notes}
                    </>
                )}
            </Paragraph>
            <Paragraph>
                Type the snapshot name to confirm:
            </Paragraph>
            <Input
                placeholder={snapshot.name}
                value={confirmName}
                onChange={(e) => setConfirmName(e.target.value)}
                status={confirmName && !nameMatches ? 'error' : undefined}
            />
            <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
                <Form.Item label="Reason (optional)" name="notes">
                    <Input.TextArea
                        rows={2}
                        placeholder="Why are you restoring this snapshot?"
                    />
                </Form.Item>
            </Form>
        </Modal>
    );
};
